import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authService";
import { workshopService } from "../services/dataService";
import { Workshop } from "../types";
import AlertModal from "../components/AlertModal";
import PageLoader from "../components/PageLoader";
import "./PurchaseOrders.css";
import "./Register.css";

interface RegisterForm {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: string;
  workshopIds: string[]; // Changed from workshopId to workshopIds array
}

const userRoles = [
  { value: "Üretim", label: "Üretim" },
  { value: "Sekreterya", label: "Sekreterya" },
  { value: "Manager", label: "Manager" },
  { value: "Modelist", label: "Desinatör" },
  { value: "Muhasebe", label: "Muhasebe" },
];

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [formData, setFormData] = useState<RegisterForm>({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    role: "Üretim", // Varsayılan rol
    workshopIds: [], // Initialize as empty array
  });

  const [errors, setErrors] = useState<Partial<RegisterForm>>({});

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Atölyeleri yükle
  useEffect(() => {
    const loadWorkshops = async () => {
      try {
        const data = await workshopService.getAll();
        // Atölyeleri displayOrder'a göre sırala
        const sortedData = [...data].sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name, "tr");
        });
        setWorkshops(sortedData);
      } catch (error) {
        console.error("Atölyeler yüklenemedi:", error);
      }
    };
    loadWorkshops();
  }, []);

  // Yetki kontrolü - Manager değilse anında yönlendir
  useEffect(() => {
    if (!isLoading && (!user || user.role !== "Manager")) {
      navigate("/orders");
    }
  }, [user, isLoading, navigate]);

  // Yetki kontrolü - Manager değilse hiçbir şey render etme
  if (isLoading) return <PageLoader />;

  if (!user || user.role !== "Manager") {
    return null; // Hiçbir şey gösterme
  }

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Partial<RegisterForm> = {};

    if (!formData.email.trim()) {
      newErrors.email = "E-posta gereklidir";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Geçerli bir e-posta adresi giriniz";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Şifre gereklidir";
    } else if (formData.password.length < 6) {
      newErrors.password = "Şifre en az 6 karakter olmalıdır";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Şifreler eşleşmiyor";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Ad gereklidir";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Soyad gereklidir";
    }

    // Üretim ve Desinatör rolleri için atölye seçimi zorunlu
    if (
      (formData.role === "Üretim" || formData.role === "Modelist") &&
      formData.workshopIds.length === 0
    ) {
      newErrors.workshopIds =
        "Bu rol için en az bir atölye seçimi zorunludur" as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Rol değiştiğinde ve seçilen rol Üretim veya Modelist değilse, workshopIds'yi temizle
      ...(name === "role" &&
        value !== "Üretim" &&
        value !== "Modelist" && { workshopIds: [] }),
    }));

    // Clear error when user starts typing
    if (errors[name as keyof RegisterForm]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const toggleWorkshop = (workshopId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      workshopIds: checked
        ? [...prev.workshopIds, workshopId]
        : prev.workshopIds.filter((id) => id !== workshopId),
    }));
    // Clear error when user selects a workshop
    if (errors.workshopIds) {
      setErrors((prev) => ({
        ...prev,
        workshopIds: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const registerData = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
        workshopIds:
          formData.workshopIds.length > 0 ? formData.workshopIds : undefined,
        isActive: true,
        createdBy: user?.userId || "unknown",
      };

      await authService.register(registerData);

      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${formData.firstName} ${formData.lastName}" başarıyla oluşturuldu!`,
        type: "success",
      });

      // Formu temizle
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        role: "Üretim",
        workshopIds: [],
      });
    } catch (error: any) {
      console.error("❌ Kullanıcı oluşturma hatası:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı oluşturulamadı: " +
          (typeof error.response?.data === "string" && error.response.data.trim()
            ? error.response.data
            : error.response?.data?.message || error.response?.data?.title || error.message),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const needsWorkshop = formData.role === "Üretim" || formData.role === "Modelist";
  const errCount = Object.values(errors).filter(Boolean).length;

  return (
    <div className="po-page rg">
      <header className="po-head">
        <div>
          <span className="po-kicker">Yönetim</span>
          <h1>Yeni kullanıcı</h1>
          <p>Sisteme giriş yapacak yeni bir kullanıcı hesabı açın; rolü ve gerekiyorsa atölyelerini seçin.</p>
        </div>
      </header>

      <section className="po-card rg-card">
        <form onSubmit={handleSubmit} className="rg-form">
          <div className="rg-body">
            <div className="po-form-sec">
              <h4>Kişisel bilgiler</h4>
              <div className="po-grid2">
                <label className="po-field">
                  <span>Ad<em>zorunlu</em></span>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange}
                    className={errors.firstName ? "is-invalid" : ""} aria-invalid={!!errors.firstName} placeholder="Adı" autoFocus required />
                  {errors.firstName && <span className="po-fielderr">{errors.firstName}</span>}
                </label>
                <label className="po-field">
                  <span>Soyad<em>zorunlu</em></span>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange}
                    className={errors.lastName ? "is-invalid" : ""} aria-invalid={!!errors.lastName} placeholder="Soyadı" required />
                  {errors.lastName && <span className="po-fielderr">{errors.lastName}</span>}
                </label>
                <label className="po-field span2">
                  <span>E-posta<em>zorunlu · giriş adı</em></span>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange}
                    className={errors.email ? "is-invalid" : ""} aria-invalid={!!errors.email} placeholder="ornek@firma.com.tr" autoComplete="off" required />
                  {errors.email && <span className="po-fielderr">{errors.email}</span>}
                </label>
              </div>
            </div>

            <div className="po-form-sec">
              <h4>Şifre</h4>
              <div className="po-grid2">
                <label className="po-field">
                  <span>Şifre<em>en az 6 karakter</em></span>
                  <input type="password" id="password" name="password" value={formData.password} onChange={handleInputChange}
                    className={errors.password ? "is-invalid" : ""} aria-invalid={!!errors.password} autoComplete="new-password" required />
                  {errors.password && <span className="po-fielderr">{errors.password}</span>}
                </label>
                <label className="po-field">
                  <span>Şifre tekrar</span>
                  <input type="password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange}
                    className={errors.confirmPassword ? "is-invalid" : ""} aria-invalid={!!errors.confirmPassword} autoComplete="new-password" required />
                  {errors.confirmPassword && <span className="po-fielderr">{errors.confirmPassword}</span>}
                </label>
              </div>
            </div>

            <div className="po-form-sec">
              <h4>Yetki</h4>
              <div className="po-grid2">
                <label className="po-field">
                  <span>Kullanıcı rolü<em>zorunlu</em></span>
                  <select id="role" name="role" value={formData.role} onChange={handleInputChange} className="po-select" required>
                    {userRoles.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  <span className="po-fieldnote">Üretim ve Desinatör rolleri için atölye seçimi zorunludur.</span>
                </label>
              </div>

              {/* Atölye seçimi Üretim ve Desinatör rolleri için */}
              {needsWorkshop && (
                <div className="po-field">
                  <span>Atölyeler<em>en az bir atölye</em></span>
                  {workshops.length === 0 ? (
                    <div className="po-blank small"><b>Tanımlı atölye yok</b>Önce Atölyeler ekranından atölye tanımlayın.</div>
                  ) : (
                    <div className={`rg-ws ${errors.workshopIds ? "is-invalid" : ""}`}>
                      <div className="rg-ws-bar">
                        <span>{formData.workshopIds.length > 0 ? <><b>{formData.workshopIds.length}</b> / {workshops.length} atölye seçili</> : "Atölye seçilmedi"}</span>
                      </div>
                      <div className="rg-ws-grid">
                        {workshops.map((workshop) => {
                          const checked = formData.workshopIds.includes(workshop.workshopId);
                          return (
                            <label key={workshop.workshopId} className={`po-check rg-ws-item ${checked ? "on" : ""}`}>
                              <input type="checkbox" value={workshop.workshopId} checked={checked} onChange={(e) => toggleWorkshop(e.target.value, e.target.checked)} />
                              <span>{workshop.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {errors.workshopIds && <span className="po-fielderr">{String(errors.workshopIds)}</span>}
                </div>
              )}
            </div>
          </div>

          <div className="rg-foot">
            {errCount > 0 && <span className="po-fielderr po-hint">{errCount} alan düzeltilmeli</span>}
            <button type="button" onClick={() => navigate("/")} className="po-btn" disabled={loading}>Vazgeç</button>
            <button type="submit" className="po-btn primary" disabled={loading}>
              {loading ? "Oluşturuluyor..." : "Kullanıcıyı oluştur"}
            </button>
          </div>
        </form>
      </section>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default Register;
